import { type ReactNode, useMemo } from 'react';

import { initialCommandPaletteState } from '../state-machine';

import {
  CommandPaletteActionsContext,
  type CommandPaletteActionsContextValue,
  CommandPaletteStateContext,
} from './command-palette-state-context';

/**
 * Inert state + actions contexts used by the ErrorBoundary fallback in
 * {@link import('./command-palette-provider').CommandPaletteProvider}.
 *
 * Lets consumer hooks that read state or dispatch actions render
 * without throwing when the palette itself crashed. Every dispatcher
 * is a no-op; the state mirrors `initialCommandPaletteState`
 * (`closed` mode, no entered root).
 */
export function DisabledCommandPaletteStateProvider({ children }: { children: ReactNode }) {
  const actionsValue = useMemo<CommandPaletteActionsContextValue>(
    () => ({
      open: () => {},
      close: () => {},
      setQuery: () => {},
      openRoot: () => {},
      exitRoot: () => {},
      openView: (() => {}) as CommandPaletteActionsContextValue['openView'],
      exitView: () => {},
      // (Signatures match the new no-arg exit shapes.)
    }),
    [],
  );

  return (
    <CommandPaletteStateContext.Provider value={initialCommandPaletteState}>
      <CommandPaletteActionsContext.Provider value={actionsValue}>{children}</CommandPaletteActionsContext.Provider>
    </CommandPaletteStateContext.Provider>
  );
}
